import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { AlertCircle, AlertTriangle, Info, CheckCircle } from 'lucide-react'

interface ReviewStepProps {
  state: any
}

export function ReviewStep({ state }: ReviewStepProps) {
  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <AlertCircle className="h-5 w-5 text-red-500" />
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />
      case 'info':
        return <Info className="h-5 w-5 text-blue-500" />
      default:
        return null
    }
  }

  const getConditionSummary = (condition: any): string => {
    if (!condition) return 'No condition set'

    switch (condition.kind) {
      case 'metric_threshold':
        return `When ${condition.metric} ${condition.operator} ${condition.value} (${condition.aggregation} over ${Math.floor(condition.windowSec / 60)} min)`
      case 'geofence_event':
        const geofences = condition.geofenceIds?.length
          ? `${condition.geofenceIds.length} geofence(s)`
          : 'any geofence'
        return `When vehicle ${condition.direction}s ${geofences}`
      case 'hos_threshold':
        return `When ${condition.hosMetric.replace(/_/g, ' ')} ${condition.operator} ${condition.value} hours`
      case 'logical':
        return `${condition.op} of ${condition.children.length} conditions`
      default:
        return 'Custom condition'
    }
  }

  const getScopeSummary = (): string => {
    const type = state.scope_type
    const ids = state.scope_selector?.ids || []

    if (type === 'fleet') {
      return 'Entire fleet'
    } else if (ids.length === 0) {
      return `All ${type}s`
    } else {
      return `${ids.length} ${type}${ids.length > 1 ? 's' : ''}`
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-4">Review & Confirm</h3>
        <p className="text-sm text-muted-foreground mb-6">
          Review your alert rule configuration before saving
        </p>
      </div>

      <Card className="p-6 space-y-6">
        {/* Basics */}
        <div>
          <div className="flex items-center gap-3 mb-3">
            {getSeverityIcon(state.severity)}
            <h4 className="text-xl font-semibold">{state.name}</h4>
            <Badge variant={state.enabled ? 'default' : 'secondary'}>
              {state.enabled ? 'Enabled' : 'Disabled'}
            </Badge>
          </div>
          {state.description && (
            <p className="text-muted-foreground">{state.description}</p>
          )}
        </div>

        <Separator />

        {/* Scope */}
        <div className="space-y-2">
          <h5 className="font-medium flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-500" />
            Scope
          </h5>
          <p className="text-sm text-muted-foreground pl-6">
            Monitoring: <strong className="text-foreground">{getScopeSummary()}</strong>
          </p>
        </div>

        {/* Condition */}
        <div className="space-y-2">
          <h5 className="font-medium flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-500" />
            Condition
          </h5>
          <p className="text-sm text-muted-foreground pl-6">
            {getConditionSummary(state.condition)}
          </p>
        </div>

        {/* Channels */}
        <div className="space-y-2">
          <h5 className="font-medium flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-500" />
            Notification Channels
          </h5>
          <div className="pl-6 space-y-1">
            {state.channels.map((channel: any, index: number) => (
              <p key={index} className="text-sm text-muted-foreground">
                <strong className="text-foreground capitalize">{channel.type}:</strong>{' '}
                {channel.target}
              </p>
            ))}
          </div>
        </div>

        <Separator />

        {/* Evaluation Settings */}
        <div className="space-y-2">
          <h5 className="font-medium">Evaluation Settings</h5>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Evaluation window:</span>{' '}
              <strong>{state.evaluation_window_sec}s</strong>
            </div>
            <div>
              <span className="text-muted-foreground">Frequency:</span>{' '}
              <strong>{state.frequency_sec}s</strong>
            </div>
            <div>
              <span className="text-muted-foreground">Fire mode:</span>{' '}
              <strong>{state.fire_mode.replace('_', ' ')}</strong>
            </div>
            <div>
              <span className="text-muted-foreground">Suppression:</span>{' '}
              <strong>{state.suppression_window_sec}s</strong>
            </div>
          </div>
        </div>
      </Card>

      {/* JSON Preview */}
      <Card className="p-4">
        <details className="space-y-3">
          <summary className="cursor-pointer font-medium text-sm">
            View JSON (for developers)
          </summary>
          <pre className="text-xs bg-muted p-4 rounded overflow-x-auto">
            {JSON.stringify(
              {
                name: state.name,
                description: state.description,
                severity: state.severity,
                enabled: state.enabled,
                scope_type: state.scope_type,
                scope_selector: state.scope_selector,
                condition: state.condition,
                channels: state.channels,
                evaluation_window_sec: state.evaluation_window_sec,
                frequency_sec: state.frequency_sec,
                fire_mode: state.fire_mode,
                suppression_window_sec: state.suppression_window_sec,
              },
              null,
              2
            )}
          </pre>
        </details>
      </Card>
    </div>
  )
}
